/*
 * @Description: 全局App通信Provider，统一处理App内嵌页面的用户信息获取
 * 支持两种方式：
 * 1. App通信回调（主要方式）：App每次进入页面都会主动调用APP_LOGIN_SUCCESS，通知最新登录状态
 * 2. URL传递token（优化方式）：App内嵌时通过URL参数app_token传递token，可以快速初始化，但App仍会通过回调确认最新状态
 *
 * 注意：
 * - App内嵌使用app_token参数，第三方登录使用token参数，避免冲突
 * - 个人中心页面（/account/*）有自己的服务端处理逻辑（middleware + layout.tsx）
 */
'use client';

import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { usePathname, useRouter } from 'next/navigation';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

interface AppCommunicationContextType {
  /** 是否在App内嵌环境 */
  isInApp: boolean;
  /** 用户信息是否已准备完成 */
  isUserInfoReady: boolean;
}

const AppCommunicationContext = createContext<AppCommunicationContextType>({
  isInApp: false,
  isUserInfoReady: false,
});

interface AppCommunicationProviderProps {
  children: React.ReactNode;
  /** 设备类型，用于判断是否在App内 */
  deviceType?: EPlatform;
}

/**
 * 全局App通信Provider
 * 统一处理App内嵌页面的用户信息获取逻辑
 */
export function AppCommunicationProvider({ children, deviceType }: AppCommunicationProviderProps) {
  const [isInApp, setIsInApp] = useState(false);
  const [isUserInfoReady, setIsUserInfoReady] = useState(false);
  const hasInitialized = useRef(false);
  const urlTokenRef = useRef<string | null>(null); // 记录URL中的token，用于去重
  const router = useRouter();
  const pathname = usePathname();

  // 判断是否是个人中心页面（个人中心页面有自己的服务端处理逻辑，不需要客户端重新获取用户信息）
  const isAccountPage = pathname?.startsWith('/account') ?? false;

  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  /**
   * 清理URL中的app_token参数
   * @param logMessage 可选的日志消息，用于标识清理操作的场景
   * @param delay 延迟执行时间（毫秒），默认100ms，避免在初始化过程中触发页面重新渲染
   */
  const cleanUrlToken = (logMessage?: string, delay = 100) => {
    setTimeout(() => {
      try {
        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.has('app_token')) {
          currentUrl.searchParams.delete('app_token');
          const newPath =
            currentUrl.pathname +
            (currentUrl.search ? currentUrl.search : '') +
            (currentUrl.hash ? currentUrl.hash : '');
          router.replace(newPath, { scroll: false });
          if (logMessage) {
            logger.log(logMessage);
          }
        }
      } catch (error) {
        logger.error('[AppCommunicationProvider] 清理URL app_token参数失败:', error);
      }
    }, delay);
  };

  // 检测是否在App内
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;
      const isInAppEnv =
        userAgent.includes('nextmar-xms-app') ||
        deviceType === EPlatform.ANDROID ||
        deviceType === EPlatform.IOS;
      setIsInApp(isInAppEnv);

      // 个人中心页面完全跳过所有逻辑，直接标记为准备完成
      if (isAccountPage) {
        logger.log('[AppCommunicationProvider] 个人中心页面，跳过所有App通信逻辑');
        return;
      }

      setIsUserInfoReady(!isInAppEnv); // 非App环境默认为准备完成

      logger.log('[AppCommunicationProvider] App环境状态已更新', isInAppEnv);
    }
  }, [deviceType, isAccountPage]);

  // App通信初始化（主要方式）
  // App每次进入页面都会主动调用APP_LOGIN_SUCCESS，通知最新登录状态
  // registerCallback会生成一个随机方法名，通过postMessage告诉App
  // App收到后会调用这个随机方法名，传递最新的token状态（可能是token或'ClearToken'）
  const handleAppCommunication = async () => {
    if (!isInApp) {
      return;
    }

    if (hasInitialized.current) {
      return;
    }

    // 个人中心页面完全跳过App通信逻辑
    if (isAccountPage) {
      return;
    }

    try {
      logger.log('[AppCommunicationProvider] 开始App通信初始化');
      hasInitialized.current = true;

      // 非个人中心页面：只有在URL中没有token的情况下才清空用户信息
      // 如果URL中已有token，说明已经设置过用户信息，不应该清空
      // App回调会确认最新状态，如果token不同或需要清除，会在回调中处理
      if (!urlTokenRef.current) {
        logger.log('[AppCommunicationProvider] URL中无token，清空当前用户信息，等待App通知');
        setToken('');
        setUserInfo({});
      } else {
        logger.log('[AppCommunicationProvider] URL中已有token，保留当前用户信息，等待App回调确认');
      }

      // 注册token回调通信
      logger.log('[AppCommunicationProvider] 注册App登录成功回调');

      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (_token) => {
          logger.log('[AppCommunicationProvider] App登录token回调触发');

          if (_token !== 'ClearToken') {
            // 检查token是否与当前token相同（避免重复设置）
            const isTokenUnchanged = urlTokenRef.current && urlTokenRef.current === _token;

            if (isTokenUnchanged) {
              logger.log('[AppCommunicationProvider] App回调token与当前token相同，跳过重复设置');

              // 在App回调确认token相同后，清理URL中的app_token参数
              // 此时用户信息已经设置完成，清理URL不会影响初始化流程
              if (urlTokenRef.current) {
                cleanUrlToken(
                  '[AppCommunicationProvider] 已清理URL中的app_token参数（App回调确认后）',
                );
                urlTokenRef.current = null; // 清空引用，避免重复清理
              }

              setIsUserInfoReady(true);
              return;
            }

            logger.log('[AppCommunicationProvider] 设置App回调的token并获取用户信息');

            // 如果URL中有旧的app_token，先清理（App回调的token是新的，应该使用新的）
            if (urlTokenRef.current && urlTokenRef.current !== _token) {
              cleanUrlToken(
                '[AppCommunicationProvider] 已清理URL中的旧app_token参数（App回调token不同）',
              );
              urlTokenRef.current = null; // 清空引用
            }
            logger.log('[AppCommunicationProvider] 设置App回调的token:', _token);
            setToken(_token);

            // 获取用户信息
            // 个人中心页面：如果token变化，仍然需要刷新用户信息（可能用户在其他地方登录）
            await refreshAccountInfo();
            logger.log('[AppCommunicationProvider] 账户信息刷新完成');

            setTimeout(() => {
              setIsUserInfoReady(true);
              logger.log('[AppCommunicationProvider] 用户信息准备完成（延迟1秒后）');
            }, 1000);
          } else {
            // ClearToken表示用户已退出登录，需要清空token和用户信息
            logger.log('[AppCommunicationProvider] App通知：用户已退出登录（ClearToken）');
            setToken('');
            setUserInfo({});
            urlTokenRef.current = null; // 清空URL token引用
            setIsUserInfoReady(true);
            logger.log('[AppCommunicationProvider] 用户信息准备完成（已退出登录）');
          }
        },
        {},
        'BackTokenInfo' as any,
      );

      logger.log('[AppCommunicationProvider] App通信回调注册成功');
    } catch (e) {
      logger.error('[AppCommunicationProvider] App通信初始化失败:', e);
      // 初始化失败时重置标记，允许重试
      hasInitialized.current = false;
      // 失败时也标记为准备完成，避免页面一直不加载
      setIsUserInfoReady(true);
      logger.log('[AppCommunicationProvider] 初始化失败，已重置状态并标记用户信息准备完成');
    }
  };

  // 从URL获取token（优化方式，快速初始化）
  // App内嵌通过app_token参数传递token，先设置以快速初始化，但App仍会通过回调确认最新状态
  // 注意：只读取app_token参数，不读取token参数（token参数保留给第三方登录使用）
  useEffect(() => {
    if (!isInApp || hasInitialized.current) return;

    // 个人中心页面有自己的服务端处理逻辑（通过 middleware 和 layout.tsx），
    // 已经在服务端获取了用户信息，完全跳过客户端的用户信息获取和App通信回调
    if (isAccountPage) {
      logger.log('[AppCommunicationProvider] 个人中心页面，完全跳过所有App通信逻辑');
      return;
    }

    // 非个人中心页面，执行原有的客户端用户信息获取逻辑
    setToken('');
    setUserInfo({});
    logger.log('[AppCommunicationProvider] 清空token和用户信息（初始化）');
    const handleUrlToken = async () => {
      logger.log('[AppCommunicationProvider] 开始处理URL app_token');

      try {
        const url = new URL(window.location.href);
        // App内嵌统一使用 app_token 参数，第三方登录使用 token 参数，避免冲突
        const tokenFromUrl = url.searchParams.get('app_token');

        if (tokenFromUrl) {
          // 记录URL中的token，用于后续去重判断
          urlTokenRef.current = tokenFromUrl;

          // 立即清理URL中的app_token参数，避免被第三方登录逻辑误判
          cleanUrlToken(
            '[AppCommunicationProvider] 已清理URL中的app_token参数（避免第三方登录误判）',
            0,
          );

          // URL中有token，先快速设置（优化首次加载体验）
          logger.log('[AppCommunicationProvider] 从URL获取到token（快速初始化）:', tokenFromUrl);
          setToken(tokenFromUrl);
          await refreshAccountInfo();
          logger.log('[AppCommunicationProvider] 账户信息刷新完成（URL token）');

          // 注意：即使URL有token，App仍会通过回调通知最新状态
          // 如果App回调的token与URL token相同，会跳过重复设置，避免触发token监听
        } else {
          logger.log('[AppCommunicationProvider] URL中未找到app_token参数');
          setToken('');
          setUserInfo({});
          logger.log('[AppCommunicationProvider] 清空token和用户信息（URL中未找到app_token参数）');
        }
      } catch (error) {
        logger.error('[AppCommunicationProvider] 从URL获取token失败:', error);
      }

      // 无论URL是否有token，都需要注册App通信回调
      // 因为App会主动调用，通知最新的登录状态（可能已退出登录）
      logger.log('[AppCommunicationProvider] 准备注册App通信回调');
      handleAppCommunication();
    };

    handleUrlToken();
  }, [isInApp, isAccountPage]);

  // 添加超时机制，防止App通信失败导致页面一直加载
  useEffect(() => {
    if (!isInApp || isAccountPage) {
      return;
    }

    const timeout = setTimeout(() => {
      if (!isUserInfoReady) {
        logger.warn('[AppCommunicationProvider] App通信超时，将以游客状态继续渲染页面');

        // 如果超时且URL中还有app_token，清理URL（App回调可能未触发）
        if (urlTokenRef.current) {
          cleanUrlToken('[AppCommunicationProvider] 超时后已清理URL中的app_token参数', 0);
          urlTokenRef.current = null;
        }

        setIsUserInfoReady(true);
      } else {
        logger.log('[AppCommunicationProvider] 超时检查：用户信息已准备完成，无需超时处理');
      }
    }, 5000); // 5秒超时

    return () => {
      clearTimeout(timeout);
    };
  }, [isInApp, isUserInfoReady, isAccountPage]);

  // 组件卸载时清理App通信管理器
  useEffect(() => {
    return () => {
      if (isInApp && !isAccountPage) {
        logger.log('[AppCommunicationProvider] 组件卸载，清理App通信管理器');
        appCommunicationManager.cleanup();
      } else {
        logger.log('[AppCommunicationProvider] 组件卸载，无需清理（非App环境或个人中心页面）');
      }
    };
  }, [isInApp, isAccountPage]);

  return (
    <AppCommunicationContext.Provider value={{ isInApp, isUserInfoReady }}>
      {children}
    </AppCommunicationContext.Provider>
  );
}

/**
 * Hook to access App communication context
 */
export function useAppCommunication() {
  return useContext(AppCommunicationContext);
}
